import { useCallback, useEffect, useState } from "react";
import { Loader2, UserPlus, KeyRound, Trash2, Pencil, Unlock, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";

const field = "bg-brand-obsidian border-brand-line";

export default function UsersTab() {
  const [users, setUsers] = useState(null);
  const [dialog, setDialog] = useState(null); // {mode: 'add'|'edit'|'password'|'delete', user}
  const [form, setForm] = useState({ email: "", name: "", password: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api
      .get("/users")
      .then((r) => setUsers(r.data))
      .catch((e) => {
        toast.error("Could not load users", { description: formatApiError(e) });
        setUsers([]);
      });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const open = (mode, user = null) => {
    setForm({ email: user?.email || "", name: user?.name || "", password: "" });
    setDialog({ mode, user });
  };

  const submit = async (e) => {
    e?.preventDefault();
    setBusy(true);
    const { mode, user } = dialog;
    try {
      if (mode === "add") {
        await api.post("/users", form);
        toast.success("User added", { description: `${form.email} can now log in to the Control Room.` });
      } else if (mode === "edit") {
        await api.put(`/users/${user.id}`, { email: form.email, name: form.name });
        toast.success("User updated");
      } else if (mode === "password") {
        await api.post(`/users/${user.id}/password`, { password: form.password });
        toast.success("Password reset", { description: `${user.email} can now log in with the new password.` });
      } else if (mode === "delete") {
        await api.delete(`/users/${user.id}`);
        toast.success("User removed");
      }
      setDialog(null);
      load();
    } catch (e2) {
      toast.error("Something went wrong", { description: formatApiError(e2) });
    } finally {
      setBusy(false);
    }
  };

  const unlock = async (user) => {
    try {
      await api.post(`/users/${user.id}/unlock`);
      toast.success("Account unlocked", { description: `${user.email} can try logging in again straight away.` });
      load();
    } catch (e) {
      toast.error("Could not unlock", { description: formatApiError(e) });
    }
  };

  if (!users) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-brand-crimson" data-testid="users-loading-spinner" />
      </div>
    );
  }

  const titles = { add: "Add user", edit: "Edit user", password: "Reset password", delete: "Remove user" };

  return (
    <div className="max-w-4xl space-y-6" data-testid="users-tab">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="flex items-center gap-2 font-eyebrow text-[10px] uppercase tracking-[0.25em] text-brand-crimson font-bold">
            <ShieldCheck className="h-4 w-4" /> Control Room Users
          </p>
          <p className="mt-1 text-sm text-white/50">Everyone listed here can log in at /admin/login. Reset a password here if someone gets locked out.</p>
        </div>
        <Button onClick={() => open("add")} data-testid="users-add-button" className="bg-brand-crimson hover:bg-brand-crimhover text-white">
          <UserPlus className="h-4 w-4 mr-1.5" /> Add user
        </Button>
      </div>

      <div className="rounded-lg border border-brand-line bg-brand-surface divide-y divide-brand-line" data-testid="users-list">
        {users.map((u) => (
          <div key={u.id} className="flex flex-wrap items-center justify-between gap-3 p-4" data-testid={`user-row-${u.email}`}>
            <div className="min-w-0">
              <p className="text-white font-semibold truncate">
                {u.name || u.email}
                {u.is_you && <span className="ml-2 text-[10px] uppercase tracking-widest text-brand-crimson">You</span>}
                {u.locked && (
                  <span className="ml-2 text-[10px] uppercase tracking-widest text-amber-400" data-testid={`user-locked-${u.email}`}>
                    Locked (too many attempts)
                  </span>
                )}
              </p>
              <p className="text-sm text-white/50 truncate">{u.email}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {u.locked && (
                <Button size="sm" variant="outline" onClick={() => unlock(u)} data-testid={`user-unlock-${u.email}`} className="border-brand-line bg-transparent text-white hover:bg-white/10">
                  <Unlock className="h-4 w-4 mr-1" /> Unlock
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={() => open("edit", u)} data-testid={`user-edit-${u.email}`} className="border-brand-line bg-transparent text-white hover:bg-white/10">
                <Pencil className="h-4 w-4 mr-1" /> Edit
              </Button>
              <Button size="sm" variant="outline" onClick={() => open("password", u)} data-testid={`user-password-${u.email}`} className="border-brand-line bg-transparent text-white hover:bg-white/10">
                <KeyRound className="h-4 w-4 mr-1" /> Reset password
              </Button>
              {!u.is_you && (
                <Button size="sm" variant="outline" onClick={() => open("delete", u)} data-testid={`user-delete-${u.email}`} className="border-red-500/40 bg-transparent text-red-400 hover:bg-red-500/10">
                  <Trash2 className="h-4 w-4 mr-1" /> Remove
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="bg-brand-surface border-brand-line text-white" data-testid="users-dialog">
          <DialogHeader>
            <DialogTitle>{dialog && titles[dialog.mode]}</DialogTitle>
          </DialogHeader>
          {dialog && (
            <form onSubmit={submit} className="space-y-4">
              {dialog.mode === "delete" ? (
                <p className="text-sm text-white/70">
                  Remove <strong>{dialog.user.email}</strong>? They will no longer be able to log in.
                </p>
              ) : (
                <>
                  {(dialog.mode === "add" || dialog.mode === "edit") && (
                    <>
                      <div className="space-y-2">
                        <Label className="text-white/80 text-sm">Name</Label>
                        <Input data-testid="users-form-name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className={field} />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-white/80 text-sm">Email (used to log in)</Label>
                        <Input data-testid="users-form-email" type="email" required value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} className={field} />
                      </div>
                    </>
                  )}
                  {dialog.mode === "password" && <p className="text-sm text-white/60">Set a new password for {dialog.user.email}.</p>}
                  {(dialog.mode === "add" || dialog.mode === "password") && (
                    <div className="space-y-2">
                      <Label className="text-white/80 text-sm">{dialog.mode === "add" ? "Password" : "New password"} (min 8 characters)</Label>
                      <Input data-testid="users-form-password" type="text" autoComplete="new-password" required minLength={8} value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} className={field} />
                    </div>
                  )}
                </>
              )}
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialog(null)} className="border-brand-line bg-transparent text-white hover:bg-white/10">
                  Cancel
                </Button>
                <Button type="submit" disabled={busy} data-testid="users-form-submit" className={dialog.mode === "delete" ? "bg-red-600 hover:bg-red-700 text-white" : "bg-brand-crimson hover:bg-brand-crimhover text-white"}>
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : dialog.mode === "delete" ? "Remove" : "Save"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
